<template>
  <section id="about" class="main intro">
    <!-- Headline Start -->
    <div id="headline" class="headline d-flex align-items-start flex-column" data-speed="1">
      <p class="headline__subtitle animate-headline">
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="13px" height="13px" viewBox="0 0 13 13"
          fill="currentColor">
          <path fill="currentColor" d="M5.6,12.6c-0.5-0.8-0.7-2.4-1.7-3.5c-1-1-2.7-1.2-3.5-1.7C-0.1,7-0.1,6,0.4,5.6c0.8-0.5,2.3-0.6,3.5-1.8
            C5,2.8,5.1,1.2,5.6,0.4C6-0.1,7-0.1,7.4,0.4c0.5,0.8,0.7,2.4,1.8,3.5c1.2,1.2,2.6,1.2,3.5,1.7c0.6,0.4,0.6,1.4,0,1.7
            C11.8,7.9,10.2,8,9.1,9.1c-1,1-1.2,2.7-1.7,3.5C7,13.1,6,13.1,5.6,12.6z" />
        </svg>
        <span>About Me</span>
      </p>
      <h3 class="headline__title animate-headline">
        <span class="typewriter" ref="beforeText">Experienced QA Engineer (</span><count-to v-if="showCounter" :end-val="totalYears" :duration="5500"></count-to><span class="typewriter" ref="afterText">+ years) dedicated to manual & automating tests, validating APIs, and stress-testing applications with K6. I bridge the gap between development and quality to deliver flawless digital products.</span>
      </h3>
      <div class="headline__btnholder d-flex flex-column flex-sm-row animate-headline">
        <a class="btn mobile-vertical btn-default btn-hover btn-hover-accent-mobile" href="#experience">
          <span class="btn-caption">My Works</span>
        </a>
      </div>
    </div>
    <!-- Headline End -->

    <!-- Scroll Button Start -->
    <div class="rotating-btn">
      <a href="#tools" class="rotating-btn__link slide-down">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
          viewBox="0 0 120 120" style="enable-background: new 0 0 120 120" xml:space="preserve" class="animate-rotation"
          data-value="360">
          <defs>
            <path id="textPath" d="M110,59.5c0,27.6-22.4,50-50,50s-50-22.4-50-50s22.4-50,50-50S110,31.9,110,59.5z" />
          </defs>
          <g>
            <use xlink:href="#textPath" fill="none"></use>
            <text>
              <textPath xlink:href="#textPath">Scroll for More * Scroll for More *</textPath>
            </text>
          </g>
        </svg>
        <i class="fa-solid fa-arrow-down bounce-arrow"></i>
      </a>
    </div>
    <!-- Scroll Button End -->
  </section>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { resumeItems, currentDate } from './data.js'
import { CountTo } from 'vue3-count-to'

export default {
  components: {
    CountTo
  },
  data() {
    return {
      showCounter: false,
    };
  },
  methods: {
    trackDownload() {
      if (typeof gtag !== 'undefined') {
        gtag('event', 'download', {
          'event_category': 'resume',
          'event_label': 'CV Download',
          'value': 1
        });
      }
      console.log('CV downloaded');
    },
    parsePeriod(period, currentDate) {
      if (period.includes(' - ')) {
        let [start, end] = period.split(' - ');
        let startDate = this.parseStartDate(start);
        let endDate = end === 'Present' ? currentDate : this.parseEndDate(end);
        return { startDate, endDate };
      }
      return { startDate: null, endDate: null };
    },
    parseStartDate(str) {
      const [monthStr, yearStr] = str.split(' ');
      const month = this.monthToNumber(monthStr);
      const year = parseInt(yearStr);
      return new Date(year, month, 1);
    },
    parseEndDate(str) {
      const [monthStr, yearStr] = str.split(' ');
      const month = this.monthToNumber(monthStr);
      const year = parseInt(yearStr);
      return new Date(year, month + 1, 0);
    },
    monthToNumber(monthStr) {
      const months = {
        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
      };
      return months[monthStr] || 0;
    }
  },
  computed: {
    totalYears() {
      let totalMs = 0;
      if (resumeItems && Array.isArray(resumeItems)) {
        resumeItems.forEach(item => {
          const { startDate, endDate } = this.parsePeriod(item.year, currentDate) || {};
          if (startDate && endDate) {
            totalMs += endDate - startDate;
          }
        });
        const totalYears = totalMs / (365.25 * 24 * 60 * 60 * 1000);
        return Math.floor(totalYears) || 0;
      }
      return 0;
    }
  },
  mounted() {
    console.log('Mounted', this.totalYears, resumeItems, currentDate);
    gsap.registerPlugin(ScrollTrigger);
    gsap.from('.animate-headline', {
      duration: 1,
      y: 50,
      opacity: 0,
      stagger: 0.2,
      ease: 'power2.out',
    });
    gsap.to('.animate-rotation', {
      rotation: 360,
      repeat: -1,
      duration: 8,
      ease: 'none',
    });

    const beforeText = this.$refs.beforeText;
    const afterText = this.$refs.afterText;
    const beforeContent = "Experienced QA Engineer (";
    const afterContent = "+ years) dedicated to manual & automating tests, validating APIs, and stress-testing applications with K6. I bridge the gap between development and quality to deliver flawless digital products.";
    let index = 0;

    const typeBefore = () => {
      if (index < beforeContent.length) {
        beforeText.textContent += beforeContent.charAt(index);
        index++;
        setTimeout(typeBefore, 30);
      } else {
        this.showCounter = true;
        index = 0;
        typeAfter();
      }
    };

    const typeAfter = () => {
      if (index < afterContent.length) {
        afterText.textContent += afterContent.charAt(index);
        index++;
        setTimeout(typeAfter, 30);
      }
    };

    beforeText.textContent = '';
    afterText.textContent = '';
    typeBefore();
  },
}
</script>

<style scoped>
:root {
  --stroke-elements: #ccc;
  --_font-accent: 'Arial', sans-serif;
  --t-bright--dark: #fff;
}

.headline__subtitle {
  display: inline-flex;
  align-items: center;
  height: 3.4rem;
  padding: 0 1.4rem;
  border-radius: var(--_radius-s, 4px);
  border: 1px solid var(--stroke-elements, #ccc);
  margin-bottom: 1rem;
}

.headline__title {
  font: normal 700 1.8rem/1.2 var(--_font-accent, 'Arial', sans-serif);
  color: var(--t-bright--dark, #fff);
  margin-bottom: 1.5rem;
}

.headline__title count-to {
  display: inline;
  color: inherit;
  font: inherit;
}

.typewriter {
  display: inline;
}

.rotating-btn__link {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  color: #416294;
  font-size: 2.2rem;
  position: relative;
}

.bounce-arrow {
  animation: bounceDown 1.8s infinite;
  font-size: 4.5rem;
  color: azure;
  z-index: 10;
}

@keyframes bounceDown {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
}

.btn-caption {
  color: var(--t-medium, #666);
}

.btn-caption:hover {
  color: var(--t-bright--dark, #fff);
}

.btn-hover:hover {
  -moz-border-radius: var(--_radius-xl, 8px);
  border-radius: var(--_radius-xl, 8px);
  border: none;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 0 16px rgba(239, 29, 242, 0.725);
  color: var(--t-bright--dark, #fff);
  backdrop-filter: blur(10px);
}

.btn-hover-outline-mobile:hover {
  border-color: #AD8330;
}

@media (min-width: 768px) {
  .headline__title {
    font-size: 3.6rem;
  }
  .rotating-btn__link {
    width: 140px;
    height: 140px;
  }
}
</style>
